import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { http, unwrapApiResponse } from '../api/http';
import { PageHeader } from '../components/PageHeader';
import { ErrorBanner } from '../components/ErrorBanner';
import { useAuth } from '../auth/AuthContext';
import { useAppUi } from '../app/useAppUi';

const MONTH_OPTIONS = [1, 3, 6, 12];

const todayTashkent = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Tashkent' }).format(new Date());

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('ru-RU', { timeZone: 'Asia/Tashkent' });
};

// ends_on is a plain day (YYYY-MM-DD); show it as dd.mm.yyyy without timezone shifts.
const fmtDay = (s) => (s ? s.split('-').reverse().join('.') : '—');

const emptyPayment = () => ({ amount: '', currency: 'USD', starts_on: todayTashkent(), months: 1, ends_on: '', note: '' });

function AccessChip({ access, t }) {
  if (!access) return null;
  if (access.kind === 'paid') return <Chip size="small" color="success" label={t('subscriptions.statusPaid')} />;
  if (access.kind === 'trial') return <Chip size="small" color="info" label={t('subscriptions.statusTrial')} />;
  if (access.kind === 'expired') return <Chip size="small" color="error" label={t('subscriptions.statusExpired')} />;
  return <Chip size="small" variant="outlined" label={t('subscriptions.statusTrialEnded')} />;
}

export function SubscriptionsPage() {
  const { t } = useAppUi();
  const { admin } = useAuth();
  const canWrite = admin?.role === 'SUPER_ADMIN' || admin?.role === 'ADMIN';

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [search, setSearch] = React.useState('');

  const [selected, setSelected] = React.useState(null);
  const [payOpen, setPayOpen] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [trialOpen, setTrialOpen] = React.useState(false);
  const [payment, setPayment] = React.useState(emptyPayment);
  const [customEnd, setCustomEnd] = React.useState(false);
  const [history, setHistory] = React.useState([]);
  const [trialDate, setTrialDate] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get('/admin/subscriptions', { params: { search: search.trim() || undefined } });
      setRows(unwrapApiResponse(res) || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [search]);

  React.useEffect(() => {
    const id = setTimeout(fetchData, 250);
    return () => clearTimeout(id);
  }, [fetchData]);

  const loadHistory = async (row) => {
    const res = await http.get(`/admin/subscriptions/${row.id}/payments`);
    setHistory(unwrapApiResponse(res) || []);
  };

  const openPay = (row) => {
    setSelected(row);
    // A renewal starts the day after the current paid period ends.
    const next = row.paid_until && row.access?.kind === 'paid'
      ? new Date(Date.parse(`${row.paid_until}T00:00:00Z`) + 86400000).toISOString().slice(0, 10)
      : todayTashkent();
    setPayment({ ...emptyPayment(), starts_on: next });
    setCustomEnd(false);
    setPayOpen(true);
  };

  const openHistory = async (row) => {
    setSelected(row);
    setHistory([]);
    setHistoryOpen(true);
    try {
      await loadHistory(row);
    } catch (e) {
      setError(e);
    }
  };

  const openTrial = (row) => {
    setSelected(row);
    const d = row.trial_ends_at ? new Date(row.trial_ends_at) : new Date(Date.now() + 3 * 86400000);
    setTrialDate(new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Tashkent' }).format(d));
    setTrialOpen(true);
  };

  const run = async (fn) => {
    setSaving(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const savePayment = () =>
    run(async () => {
      const body = {
        user_id: selected.id,
        amount: Number(payment.amount || 0),
        currency: payment.currency,
        starts_on: payment.starts_on,
        note: payment.note,
      };
      if (customEnd) body.ends_on = payment.ends_on;
      else body.months = Number(payment.months);
      await http.post('/admin/subscriptions', body);
      setPayOpen(false);
      await fetchData();
    });

  const deletePayment = (p) =>
    run(async () => {
      await http.delete(`/admin/subscriptions/payments/${p.id}`);
      await loadHistory(selected);
      await fetchData();
    });

  const saveTrial = () =>
    run(async () => {
      // End of the chosen day in Tashkent (UTC+5).
      await http.put(`/admin/subscriptions/${selected.id}/trial`, { trial_ends_at: `${trialDate}T23:59:59+05:00` });
      setTrialOpen(false);
      await fetchData();
    });

  function ActionsCell({ row }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const close = () => setAnchorEl(null);
    const act = (fn) => () => {
      close();
      fn(row);
    };
    return (
      <>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} aria-label={t('subscriptions.colActions')}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
          <MenuItem disabled={!canWrite} onClick={act(openPay)}>{t('subscriptions.addPayment')}</MenuItem>
          <MenuItem onClick={act(openHistory)}>{t('subscriptions.history')}</MenuItem>
          <MenuItem disabled={!canWrite} onClick={act(openTrial)}>{t('subscriptions.setTrial')}</MenuItem>
        </Menu>
      </>
    );
  }

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'agency_name',
      headerName: t('subscriptions.colAccount'),
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>{row.agency_name || row.name || `#${row.id}`}</Typography>
          <Typography variant="caption" color="text.secondary">{row.email || row.phone_number || ''}</Typography>
        </Box>
      ),
    },
    {
      field: 'access',
      headerName: t('subscriptions.colStatus'),
      width: 130,
      sortable: false,
      renderCell: ({ row }) => <AccessChip access={row.access} t={t} />,
    },
    {
      field: 'paid_until',
      headerName: t('subscriptions.colPaidUntil'),
      width: 130,
      valueFormatter: (v) => fmtDay(v),
    },
    {
      field: 'trial_ends_at',
      headerName: t('subscriptions.colTrialUntil'),
      width: 130,
      valueFormatter: (v) => fmtDate(v),
    },
    {
      field: 'last_amount',
      headerName: t('subscriptions.colLastPayment'),
      width: 130,
      valueGetter: (_, row) => (row.last_amount != null ? `${Number(row.last_amount)} ${row.last_currency}` : '—'),
    },
    {
      field: 'total_usd',
      headerName: t('subscriptions.colTotal'),
      width: 110,
      valueGetter: (_, row) => (row.total_usd != null ? `$${Number(row.total_usd)}` : '—'),
    },
    {
      field: 'partners',
      headerName: t('subscriptions.colPartners'),
      width: 170,
      sortable: false,
      valueGetter: (_, row) => (row.partners || []).map((p) => `#${p.id} ${p.name}`).join(', ') || '—',
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: ({ row }) => <ActionsCell row={row} />,
    },
  ];

  const paidCount = rows.filter((r) => r.access?.kind === 'paid').length;
  const trialCount = rows.filter((r) => r.access?.kind === 'trial').length;

  return (
    <Box>
      <PageHeader
        title={t('subscriptions.title')}
        subtitle={t('subscriptions.subtitle', { paid: paidCount, trial: trialCount, total: rows.length })}
        right={
          <Stack direction="row" spacing={1}>
            <TextField size="small" placeholder={t('subscriptions.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
            <Button variant="outlined" onClick={fetchData} disabled={loading}>{t('common.refresh')}</Button>
          </Stack>
        }
      />

      <ErrorBanner error={error} />

      <Box sx={{ height: 680, bgcolor: 'background.paper', borderRadius: 2, boxShadow: (th) => th.custom.shadow, overflow: 'hidden' }}>
        <DataGrid rows={rows} columns={columns} loading={loading} getRowHeight={() => 'auto'} disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }} pageSizeOptions={[20, 50, 100]} />
      </Box>

      <Dialog open={payOpen} onClose={() => setPayOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('subscriptions.payTitle', { name: selected?.agency_name || selected?.name || '' })}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField label={t('subscriptions.amount')} type="number" value={payment.amount} fullWidth
                onChange={(e) => setPayment((p) => ({ ...p, amount: e.target.value }))} inputProps={{ min: 0 }} />
              <TextField select label={t('subscriptions.currency')} value={payment.currency} sx={{ width: 140 }}
                onChange={(e) => setPayment((p) => ({ ...p, currency: e.target.value }))}>
                {['USD', 'UZS', 'EUR'].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField label={t('subscriptions.startsOn')} type="date" value={payment.starts_on} InputLabelProps={{ shrink: true }}
              onChange={(e) => setPayment((p) => ({ ...p, starts_on: e.target.value }))} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {MONTH_OPTIONS.map((m) => (
                <Chip key={m} label={t('subscriptions.months', { n: m })} clickable
                  color={!customEnd && Number(payment.months) === m ? 'primary' : 'default'}
                  onClick={() => { setCustomEnd(false); setPayment((p) => ({ ...p, months: m })); }} />
              ))}
              <Chip label={t('subscriptions.customEnd')} clickable color={customEnd ? 'primary' : 'default'} onClick={() => setCustomEnd(true)} />
            </Stack>
            {customEnd ? (
              <TextField label={t('subscriptions.endsOn')} type="date" value={payment.ends_on} InputLabelProps={{ shrink: true }}
                onChange={(e) => setPayment((p) => ({ ...p, ends_on: e.target.value }))} />
            ) : null}
            <TextField label={t('subscriptions.note')} value={payment.note} multiline minRows={2}
              onChange={(e) => setPayment((p) => ({ ...p, note: e.target.value }))} />
            <Typography variant="caption" color="text.secondary">{t('subscriptions.payHint')}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)} disabled={saving}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={savePayment}
            disabled={saving || !payment.starts_on || (customEnd && !payment.ends_on)}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t('subscriptions.historyTitle', { name: selected?.agency_name || selected?.name || '' })}</DialogTitle>
        <DialogContent dividers>
          {history.length === 0 ? (
            <Typography color="text.secondary">{t('subscriptions.noPayments')}</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('subscriptions.period')}</TableCell>
                  <TableCell>{t('subscriptions.amount')}</TableCell>
                  <TableCell>{t('subscriptions.note')}</TableCell>
                  <TableCell>{t('subscriptions.recorded')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{fmtDay(p.starts_on)} – {fmtDay(p.ends_on)}</TableCell>
                    <TableCell>{Number(p.amount)} {p.currency}</TableCell>
                    <TableCell>{p.note || ''}</TableCell>
                    <TableCell>{fmtDate(p.created_at)}</TableCell>
                    <TableCell align="right">
                      {canWrite ? (
                        <IconButton size="small" color="error" disabled={saving} onClick={() => deletePayment(p)} aria-label={t('common.delete')}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={trialOpen} onClose={() => setTrialOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('subscriptions.trialTitle')}</DialogTitle>
        <DialogContent dividers>
          <TextField label={t('subscriptions.colTrialUntil')} type="date" value={trialDate} fullWidth InputLabelProps={{ shrink: true }}
            onChange={(e) => setTrialDate(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrialOpen(false)} disabled={saving}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={saveTrial} disabled={saving || !trialDate}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
